import React, {useRef, useState, useEffect} from "react";
import {Check, Info, HelpCircle} from "lucide-react";
import {
  Divider,
  Spacer,
  Tooltip,
} from "@heroui/react";
import {cn} from "@heroui/react";

import {pricingComparisonTiers} from "./pricing-tiers";
import features from "./pricing-tiers-features";

const NAVBAR_HEIGHT = 60;
/** 表格首列（功能名）最小宽度，吸顶条与主表保持一致 */
const FIRST_COL_MIN_WIDTH = "min-w-[12rem]";

export default function PricingComparison() {
  const headerSentinelRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const syncingScrollRef = useRef(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  /** 仅当「完整功能对比」的表格区域在视窗内为 true，避免仅露出 section 顶部就触发 */
  const [tableInView, setTableInView] = useState(false);
  /** 仅当哨兵已滚过视窗顶部（在视窗上方）为 true，哨兵在视窗下方时仍为 false */
  const [headerScrolledPast, setHeaderScrolledPast] = useState(false);

  useEffect(() => {
    const tableEl = tableScrollRef.current;
    const sentinelEl = headerSentinelRef.current;
    if (!tableEl || !sentinelEl) return;

    const tableObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setTableInView(entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: "0px" }
    );

    const headerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const notIntersecting = !entry.isIntersecting;
          const aboveViewport = entry.boundingClientRect.bottom < NAVBAR_HEIGHT;
          setHeaderScrolledPast(notIntersecting && aboveViewport);
        });
      },
      { threshold: 0, rootMargin: `-${NAVBAR_HEIGHT}px 0px 0px 0px` }
    );

    tableObserver.observe(tableEl);
    headerObserver.observe(sentinelEl);

    return () => {
      tableObserver.disconnect();
      headerObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    setStickyVisible(tableInView && headerScrolledPast);
  }, [tableInView, headerScrolledPast]);

  // 移动端：吸顶条与主表横向滚动同步
  useEffect(() => {
    if (!stickyVisible) return;
    const tableEl = tableScrollRef.current;
    const stickyEl = stickyScrollRef.current;
    if (!tableEl || !stickyEl) return;

    const syncToSticky = () => {
      if (syncingScrollRef.current) return;
      syncingScrollRef.current = true;
      stickyEl.scrollLeft = tableEl.scrollLeft;
      requestAnimationFrame(() => {
        syncingScrollRef.current = false;
      });
    };
    const syncToTable = () => {
      if (syncingScrollRef.current) return;
      syncingScrollRef.current = true;
      tableEl.scrollLeft = stickyEl.scrollLeft;
      requestAnimationFrame(() => {
        syncingScrollRef.current = false;
      });
    };

    tableEl.addEventListener("scroll", syncToSticky, { passive: true });
    stickyEl.addEventListener("scroll", syncToTable, { passive: true });
    syncToSticky();

    return () => {
      tableEl.removeEventListener("scroll", syncToSticky);
      stickyEl.removeEventListener("scroll", syncToTable);
    };
  }, [stickyVisible]);

  const firstFeatureRow = features[0]?.items[0];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col items-center pt-40 pb-16">
      {/* 吸顶条：仅当表格在视窗内且「基础能力」行已滚过顶部时显示 */}
      {stickyVisible && (
        <div
          className="border-default-100/60 bg-default-100/10 fixed left-0 right-0 z-40 border-b shadow-md backdrop-blur-md"
          style={{ top: NAVBAR_HEIGHT }}
          aria-hidden
        >
          <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
            <div className="isolate w-full">
              <div
                ref={stickyScrollRef}
                className="relative w-full overflow-x-auto overflow-y-hidden overscroll-x-contain [-webkit-overflow-scrolling:touch]"
              >
                <table className="w-full min-w-[max-content] table-auto border-separate border-spacing-x-4 text-left">
                  <thead>
                    <tr>
                      <td className={cn("w-[1px]", FIRST_COL_MIN_WIDTH)} />
                      {pricingComparisonTiers.map((tier) => (
                        <th key={tier.key} className="px-6 py-3 xl:px-8" scope="col">
                          <div className="text-medium text-default-900 font-medium text-center">
                            {tier.title}
                          </div>
                        </th>
                      ))}
                    </tr>
                    {firstFeatureRow && (
                      <tr>
                        <th className={cn("text-small text-default-500 py-2 font-normal", FIRST_COL_MIN_WIDTH)} scope="row">
                          {firstFeatureRow.title}
                        </th>
                        {pricingComparisonTiers.map((tier) => (
                          <td key={tier.key} className="px-6 py-2 xl:px-8">
                            {typeof firstFeatureRow.tiers[tier.key] === "string" ? (
                              <div className="text-small text-default-500 text-center">
                                {firstFeatureRow.tiers[tier.key] as string}
                              </div>
                            ) : (
                              <Check
                                className={cn(
                                  "mx-auto",
                                  firstFeatureRow.tiers[tier.key] === true ? "text-primary" : "text-default-300"
                                )}
                                size={20}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    )}
                  </thead>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex max-w-xl flex-col text-center">
        <h2 className="text-foreground text-2xl leading-7 font-medium sm:text-4xl">完整功能对比</h2>
        <Spacer y={4} />
        <h2 className="text-large text-default-500">
          对比各版本功能，选择最适合您团队的方案
        </h2>
      </div>
      <Spacer y={8} />

      <div className="isolate w-full">
        <div
          ref={tableScrollRef}
          className="relative w-full overflow-x-auto overflow-y-hidden overscroll-x-contain [-webkit-overflow-scrolling:touch]"
        >
          <table className="w-full min-w-[max-content] table-auto border-separate border-spacing-x-4 text-left">
            <caption className="sr-only">Pricing plan comparison</caption>
            <thead>
              <tr>
                <td className={FIRST_COL_MIN_WIDTH} />
                {pricingComparisonTiers.map((tier) => (
                  <th key={tier.key} className="px-6 pt-6 xl:px-8 xl:pt-8" scope="col">
                    <div className="text-medium text-default-900 font-medium text-center">
                      {tier.title}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((feat, featIndex) => (
                <React.Fragment key={feat.title}>
                  {featIndex > 0 && (
                    <tr>
                      <th
                        className={cn("text-large text-foreground pt-4 pb-4 font-semibold")}
                        colSpan={pricingComparisonTiers.length + 1}
                        scope="colgroup"
                      >
                        {feat.title}
                        <Divider className="bg-default-600/10 absolute -inset-x-4 mt-2" />
                      </th>
                    </tr>
                  )}
                  {featIndex === 1 && (
                    <tr aria-hidden>
                      <td colSpan={pricingComparisonTiers.length + 1} className="p-0">
                        <div ref={headerSentinelRef} className="h-px w-full" />
                      </td>
                    </tr>
                  )}
                  {feat.items.map((tierFeature, itemIndex) => (
                    <React.Fragment key={tierFeature.title}>
                      <tr>
                        <th className={cn("text-small text-default-500 py-3 font-normal", FIRST_COL_MIN_WIDTH)} scope="row">
                          {tierFeature.helpText ? (
                            <div className="flex items-center gap-1">
                              <span>{tierFeature.title}</span>
                              <Tooltip
                                className="max-w-[240px]"
                                color="default"
                                classNames={{
                                  content: "bg-default-800 py-3 px-4",
                                }}
                                content={
                                  <p className="text-[12px] text-default-300 font-regular">
                                    {tierFeature.helpText}
                                  </p>
                                }
                                placement="right"
                              >
                                {tierFeature.title === "知识单元" ? (
                                  <HelpCircle className="text-default-400" size={14} />
                                ) : (
                                  <Info className="text-default-500" size={14} />
                                )}
                              </Tooltip>
                            </div>
                          ) : (
                            tierFeature.title
                          )}
                        </th>
                        {pricingComparisonTiers.map((tier) => (
                          <td key={tier.key} className="px-6 py-3 xl:px-8">
                            {typeof tierFeature.tiers[tier.key] === "string" ? (
                              <div className="text-small text-default-500 text-center">
                                {tierFeature.tiers[tier.key] as string}
                              </div>
                            ) : (
                              <>
                                <Check
                                  className={cn("mx-auto", tierFeature.tiers[tier.key] === true ? "text-primary" : "text-default-300")}
                                  size={20}
                                />

                                <span className="sr-only">
                                  {tierFeature.tiers[tier.key] === true ? "Included" : "Not included"}
                                  &nbsp;in&nbsp;{tier.title}
                                </span>
                              </>
                            )}
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

